import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader2, Film } from 'lucide-react';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { Channel } from '@lib/streamFlowApi';

/**
 * Catalog grid for VOD movies + series. Simpler than the live ChannelGrid
 * (no region/bucket chips, no favorites/heartbeat — those belong with
 * live channels). Drives its own server query with debounced search +
 * infinite scroll; the parent page picks the `kind` filter and the
 * empty-state message.
 *
 * Movies render with onPlay(channel) -> direct playback. Series render
 * with onPlay(channel) -> series-detail navigation (episode picker
 * follows in a later phase). The parent decides what onPlay does.
 */

interface PosterGridProps {
  /** 'vod' = Movies, 'series' = TV shows. Drives the listChannels query
   * and (subtly) the empty-state copy. */
  kind: 'vod' | 'series';
  onPlay: (channel: Channel) => void;
  /** Optional category filter (raw provider category like 'ACTION',
   * 'COMEDY'). When set, server returns only matching titles. */
  category?: string | null;
}

const PAGE_SIZE = 60;

export default function PosterGrid({ kind, onPlay, category }: PosterGridProps) {
  const [search, setSearch] = useState('');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Debounced search + initial fetch. Reset on kind/category/search change.
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      setPage(1);
      const res = await streamFlowApi.listChannels({
        type: kind,
        search: search || undefined,
        category: category || undefined,
        page: 1,
        limit: PAGE_SIZE,
      });
      setLoading(false);
      if (res.success && res.data) {
        setChannels(res.data);
        setTotal(res.pagination?.total ?? res.data.length);
      } else {
        setChannels([]);
        setTotal(0);
      }
    }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [kind, search, category]);

  const loadMore = useCallback(async () => {
    if (loadingMore || channels.length >= total) return;
    setLoadingMore(true);
    const next = page + 1;
    const res = await streamFlowApi.listChannels({
      type: kind,
      search: search || undefined,
      category: category || undefined,
      page: next,
      limit: PAGE_SIZE,
    });
    setLoadingMore(false);
    if (res.success && res.data) {
      setChannels((prev) => [...prev, ...(res.data ?? [])]);
      setPage(next);
    }
  }, [loadingMore, channels.length, total, page, kind, search, category]);

  // IntersectionObserver-based infinite scroll. The sentinel div at the
  // bottom of the grid triggers loadMore when ~200px from view.
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: '200px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [loadMore]);

  const noun = kind === 'vod' ? 'movies' : 'series';

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="search"
          placeholder={`Search ${noun}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-slate-700 focus:outline-none"
        />
      </div>

      {!loading && (
        <p className="text-xs text-gray-500">
          {total.toLocaleString()} {noun} {search ? `matching "${search}"` : 'available'}
        </p>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-[2/3] bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : channels.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Film className="w-12 h-12 mb-3 text-gray-300" />
          <p className="text-sm">
            {search
              ? `No ${noun} match "${search}"`
              : `No ${noun} in your catalog yet`}
          </p>
          {!search && (
            <p className="text-xs mt-1">
              Add an Xtream provider that carries {noun} to populate this view.
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {channels.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onPlay(c)}
                className="group text-left"
              >
                <div className="aspect-[2/3] bg-gray-100 rounded-lg overflow-hidden relative">
                  {c.logo_url ? (
                    <img
                      src={c.logo_url}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => {
                        // Hide broken poster URLs (Xtream providers often
                        // ship dead links) — fall back to a neutral tile.
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs font-medium text-slate-900 line-clamp-2 group-hover:text-slate-700">
                  {c.name}
                </p>
                {c.category && (
                  <p className="text-[10px] text-gray-500 line-clamp-1">{c.category}</p>
                )}
              </button>
            ))}
          </div>
          <div ref={loadMoreRef} className="py-4 flex justify-center">
            {loadingMore && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
            {!loadingMore && channels.length >= total && total > 0 && (
              <p className="text-xs text-gray-400">All {total.toLocaleString()} {noun} loaded</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
